
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { Loader2, Save, PenLine } from "lucide-react";
import { useAppSelector } from '@/hooks/useAppSelector';
import type { QuarterlyEvent } from '@/store/quarterlyEventsSlice';
import { createEvent } from '@/utils/eventsHelper';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface EventLoggerProps {
  slug: string;
  quarter: string;
}

const EventLogger = ({ slug, quarter }: EventLoggerProps) => {
  const [compareLoading, setCompareLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [compareResult, setCompareResult] = useState<{ answer: string; average?: number } | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [quarterlyEvent, setQuarterlyEvent] = useState<QuarterlyEvent | null>(null);
  const [existingEvent, setExistingEvent] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState('');
  const [editedAverage, setEditedAverage] = useState('');

  // Get events from Redux store
  const events = useAppSelector(state => state.quarterlyEvents.events);
  const supabaseEvents = useAppSelector(state => state.supaEvents.events);

  // Clear results when slug or quarter changes
  useEffect(() => {
    setCompareResult(null);
    setCompareError(null);
    setExistingEvent(false);
    setIsEditing(false);
    setEditedAnswer('');
    setEditedAverage('');
  }, [slug, quarter]);

  // Update edited values when compareResult changes
  useEffect(() => {
    if (compareResult) {
      setEditedAnswer(compareResult.answer);
      setEditedAverage(compareResult.average?.toString() || '');
    }
  }, [compareResult]);

  // Find existing event in Supabase events
  useEffect(() => {
    if (slug && quarter && supabaseEvents.length > 0) {
      const event = supabaseEvents.find(e => e.slug === slug && e.quarter === quarter);
      if (event) {
        console.log('Found existing event:', event);
        setCompareResult({
          answer: event.description || '',
          average: event.average || 0
        });
        setExistingEvent(true);
      }
    }
  }, [slug, quarter, supabaseEvents]);

  // Find the relevant quarterly event when slug or quarter changes
  useEffect(() => {
    if (slug && quarter && events.length > 0) {
      const event = events.find(e => e.slug === slug && e.quarter === quarter);
      console.log('Found event:', event);
      setQuarterlyEvent(event || null);
    }
  }, [slug, quarter, events]);

  const handleSaveEvent = async () => {
    if (!editedAverage) return;
    
    setSaveLoading(true);
    try {
      const average = parseFloat(editedAverage);
      const type = average > 0 ? 'priceIncrease' : 'priceDecrease';
      await createEvent({
        slug,
        quarter,
        type,
        description: editedAnswer,
        average,
        fabric_json1: null,
        fabric_json2: null
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCompareGemini = async () => {
    if (!slug || !quarter) {
      setCompareError('Slug and quarter are required');
      return;
    }

    setCompareLoading(true);
    setCompareError(null);
    setCompareResult(null);

    const query = "Calculate the average price increase for the plans between the two versions. Only include plans that changed prices in the calculation. Normalize the pricing first. Provide answer as a percentage (e.g. for 50% return 0.5). Explain your calculation in the answer.";

    try {
      const { data, error } = await invokeEdgeFunction('compare-gemini', {
        slug,
        quarter,
        query,
        queryType: 'average'
      });

      if (error) {
        console.error('Error comparing with Gemini:', error);
        setCompareError(error.message || 'Error processing comparison');
        return;
      }

      if (data.answer && typeof data.average === 'number') {
        setCompareResult(data);
      } else {
        setCompareError('Received invalid response format from Gemini comparison');
      }
    } catch (error) {
      console.error('Error:', error);
      setCompareError('An unexpected error occurred');
    } finally {
      setCompareLoading(false);
    }
  };

  if (!slug || !quarter) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Slug and quarter are required</AlertDescription>
      </Alert>
    );
  }

  if (!quarterlyEvent) {
    return (
      <Alert>
        <AlertDescription>No event found for the given slug and quarter</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Version 1</h3>
          {quarterlyEvent.v1_url ? (
            <div className="relative h-[450px] w-full overflow-hidden rounded-lg border border-gray-200">
              <img
                src={quarterlyEvent.v1_url}
                alt="Version 1"
                className="w-full object-cover"
              />
            </div>
          ) : (
            <Alert>
              <AlertDescription>Version 1 image not available</AlertDescription>
            </Alert>
          )}
        </div>
        <div>
          <h3 className="text-sm font-medium mb-2">Version 2</h3>
          {quarterlyEvent.v2_url ? (
            <div className="relative h-[450px] w-full overflow-hidden rounded-lg border border-gray-200">
              <img
                src={quarterlyEvent.v2_url}
                alt="Version 2"
                className="w-full object-cover"
              />
            </div>
          ) : (
            <Alert>
              <AlertDescription>Version 2 image not available</AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Button 
          onClick={handleCompareGemini}
          disabled={compareLoading}
          className="w-full"
        >
          {compareLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Calculating Average Price Change...
            </>
          ) : (
            'Calculate Average Price Change'
          )}
        </Button>
      </div>

      {compareError && (
        <Alert variant="destructive">
          <AlertDescription>{compareError}</AlertDescription>
        </Alert>
      )}

      {compareResult && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="analysis" className="font-medium">Analysis:</label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsEditing(!isEditing)}
                className="h-8"
              >
                <PenLine className="h-4 w-4" />
              </Button>
            </div>
            {isEditing ? (
              <Textarea
                id="analysis"
                value={editedAnswer}
                onChange={(e) => setEditedAnswer(e.target.value)}
                className="min-h-[100px] bg-white"
              />
            ) : (
              <div className="font-medium">{editedAnswer}</div>
            )}
          </div>
          {typeof compareResult.average === 'number' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="average" className="font-medium">Average Change:</label>
                {isEditing ? (
                  <Input
                    id="average"
                    type="number"
                    step="0.01"
                    value={editedAverage}
                    onChange={(e) => setEditedAverage(e.target.value)}
                    className="bg-white"
                  />
                ) : (
                  <div className="font-medium">{editedAverage}</div>
                )}
              </div>
              <Button
                onClick={handleSaveEvent}
                disabled={saveLoading}
                className="w-full"
                variant="secondary"
              >
                {saveLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {existingEvent ? 
                      `Update ${parseFloat(editedAverage) > 0 ? 'Price Increase' : 'Price Decrease'} Event` :
                      `Save as ${parseFloat(editedAverage) > 0 ? 'Price Increase' : 'Price Decrease'} Event`
                    }
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventLogger;
